import * as assert from 'assert';
import * as React from 'react';
import DropZone from 'react-dropzone';
import { ACCEPT_IMAGE_TYPE, OnboardingSetupProfileConstants as ui } from '../../Config/UIConstants';
import { CommonActionButton } from '../../SharedComponents/CommonActionButton';
import { CommonTextInput } from '../../SharedComponents/CommonTextInput';
import { IUploadedPhoto } from '../../Stores/UserPhotoStore';
import { EmptyProfileImage, ModalXIconInverse } from '../../images/images';
import { colors } from '../../styles/colors';
import { fullContainer, largeShadow } from '../../styles/styleObjects';

export interface IUserProfile {
    firstName: string;
    lastName: string;
    email: string;
    nickname?: string;
    location?: string;
    birthday?: string;
    birthPlace?: string;
}

interface IProps {
    profile: IUserProfile;
    profilePhoto?: IUploadedPhoto | string;
    onChangeProfile(profile: IUserProfile): void;
    onAddPhoto(file: Array<File>): void;
    onRemovePhoto(): void;
    onPressSave?(): void;
    hasMadeChanges: boolean;
}

export class ProfileForm extends React.Component<IProps> {
    private dropzoneRef: any;

    public componentDidMount(): void {
        window.scrollTo(0, 0);
    }

    private renderProfileInput(profileKey: keyof IUserProfile) {
        let header = '';
        let placeholder = '';
        let InputComponent = CommonTextInput;
        let disabled = false;

        switch (profileKey) {
            case 'firstName':
                header = ui.FIRST_NAME_HEADER;
                placeholder = ui.REQUIRED_PLACEHOLDER;
                break;
            case 'lastName':
                header = ui.LAST_NAME_HEADER;
                placeholder = ui.REQUIRED_PLACEHOLDER;
                break;
            case 'email':
                header = ui.EMAIL_HEADER;
                placeholder = ui.REQUIRED_PLACEHOLDER;
                disabled = true;
                break;
            case 'nickname':
                header = ui.NICKNAME_HEADER;
                placeholder = ui.OPTIONAL_PLACEHOLDER;
                break;

            default:
                assert(false, 'case not accounted for @renderProfileInput');
        }
        return (
            <InputComponent
                disabled={disabled}
                value={this.props.profile[profileKey]}
                containerStyle={styles.inputContainer}
                onChangeText={(text: string) => this.props.onChangeProfile({ ...this.props.profile, [profileKey]: text })}
                header={header}
                placeholder={placeholder}
                sideButton={
                    disabled
                        ? undefined
                        : () => (
                              <div style={styles.inputCancel} onClick={() => this.props.onChangeProfile({ ...this.props.profile, [profileKey]: '' })}>
                                  <img src={ModalXIconInverse} style={styles.cancelIcon} />
                              </div>
                          )
                }
            />
        );
    }

    public state = { birthday: this.props.profile.birthday };

    private get profilePhotoURL(): string {
        const { profilePhoto } = this.props;
        if (!profilePhoto) {
            return EmptyProfileImage;
        } else if (typeof profilePhoto === 'string') {
            return profilePhoto;
        }
        return profilePhoto.image!.preview;
    }

    private get profilePhotoStyle(): React.CSSProperties {
        const { profilePhoto } = this.props;
        return typeof profilePhoto === 'object' ? { ...styles.profileImage, transform: `rotate(${profilePhoto.rotateDegrees}deg)` } : styles.profileImage;
    }

    public render() {
        return (
            <div>
                <div style={styles.contentContainer}>
                    <div style={styles.leftContainer}>
                        <div style={styles.inputColumn}>
                            {this.renderProfileInput('firstName')}
                            {this.renderProfileInput('lastName')}
                            {this.renderProfileInput('email')}
                            {this.renderProfileInput('nickname')}
                        </div>
                    </div>

                    <div style={styles.rightSideContainer}>
                        <div style={styles.dropZoneTopContainer}>
                            <DropZone
                                style={styles.dropzoneContainer}
                                ref={(ref: any) => {
                                    this.dropzoneRef = ref;
                                }}
                                onDrop={this.props.onAddPhoto}
                                accept={ACCEPT_IMAGE_TYPE}
                                disableClick={true}
                                multiple={false}
                            >
                                <p style={styles.profilePictureHeader}>{ui.PROFILE_PICTURE}</p>
                                <img style={this.profilePhotoStyle} src={this.profilePhotoURL} />
                                {this.props.profilePhoto ? (
                                    <div style={styles.profileButtonsContainer}>
                                        <a style={styles.profileButtonText} onClick={this.props.onRemovePhoto}>
                                            {ui.REMOVE}
                                        </a>
                                        <a style={styles.profileButtonText} onClick={() => this.dropzoneRef.open()}>
                                            {ui.REPLACE}
                                        </a>
                                    </div>
                                ) : (
                                    <div style={styles.bottomPadding}>
                                        <a style={styles.profileButtonText} onClick={() => this.dropzoneRef.open()}>
                                            {ui.ADD_PHOTO}
                                        </a>
                                    </div>
                                )}
                            </DropZone>
                            {this.props.onPressSave && (
                                <div style={styles.saveButtonContainer}>
                                    <CommonActionButton buttonContent={ui.SAVE} onClick={this.props.onPressSave} isDisabled={!this.props.hasMadeChanges} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const styles = {
    saveButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        height: 85,
        alignItems: 'flex-end'
    } as React.CSSProperties,
    bottomPadding: {
        marginBottom: 10
    },
    profileImage: {
        height: 180,
        width: 180,
        borderRadius: 90,
        objectFit: 'cover'
    } as React.CSSProperties,
    profileButtonsContainer: {
        display: 'flex',
        width: 180,
        height: 20,
        marginBottom: 10,
        justifyContent: 'space-between',
        paddingLeft: 20,
        paddingRight: 20
    } as React.CSSProperties,
    profileButtonText: {
        fontSize: 14,
        fontWeight: 900,
        color: colors.ACTION_GREEN,
        cursor: 'pointer'
    } as React.CSSProperties,
    profilePictureHeader: {
        color: 'black',
        fontWeight: 900,
        marginTop: 15,
        fontSize: 14
    } as React.CSSProperties,
    nextButton: {
        width: 120,
        height: 35
    },
    nextButtonText: {
        fontSize: 14
    },
    leftContainer: {
        display: 'flex',
        height: '100%',
        flex: 1,
        padding: 10,
        justifyContent: 'flex-end'
    } as React.CSSProperties,
    inputColumn: {
        width: 400,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    } as React.CSSProperties,
    rightSideContainer: {
        display: 'flex',
        height: '100%',
        flex: 1,
        padding: 10
    } as React.CSSProperties,
    dropZoneTopContainer: {
        width: 400,
        height: '100%'
    },
    dropzoneContainer: {
        height: 285,
        width: '100%',
        marginBottom: 15,
        backgroundColor: 'white',
        boxShadow: largeShadow,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between'
    } as React.CSSProperties,
    uploadTopHalf: {
        height: 205,
        flex: 1,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    } as React.CSSProperties,
    emptyProfileImage: {
        height: 155,
        width: 155,
        paddingTop: 15,
        objectFit: 'contain'
    } as React.CSSProperties,
    uploadBottomContainer: {
        height: 80,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: 10,
        paddingRight: 15
    } as React.CSSProperties,
    uploadIcon: {
        height: 45,
        width: 45,
        objectFit: 'contain',
        paddingBottom: 10
    } as React.CSSProperties,
    uploadInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    } as React.CSSProperties,
    uploadSubText: {
        fontSize: 12,
        fontStyle: 'italic',
        opacity: 0.8
    } as React.CSSProperties,
    inputContainer: {
        width: '100%',
        height: 85,
        marginBottom: 15,
        backgroundColor: 'white',
        boxShadow: largeShadow
    },
    inputCancel: {
        height: 30,
        width: 30,
        borderRadius: 15,
        position: 'relative',
        right: '-89%',
        bottom: 63,
        backgroundColor: '#00000022'
    } as React.CSSProperties,
    cancelIcon: {
        height: '100%',
        width: '100%',
        objectFit: 'contain'
    } as React.CSSProperties,
    headerText: {
        fontSize: 25,
        color: colors.DARK_TEXT,
        marginTop: 50
    },
    contentContainer: {
        marginTop: 25,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%'
    } as React.CSSProperties,
    bottomContainer: {
        height: 150,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column'
    } as React.CSSProperties,
    buttonText: {
        fontSize: 14,
        fontWeight: 200,
        letterSpacing: 1
    } as React.CSSProperties,
    buttonContainer: {
        height: 48,
        width: 180
    },
    activityIndicator: {
        width: 80,
        height: 15,
        marginTop: 35
    },
    container: {
        borderRadius: 2
    },
    input: {
        outline: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        paddingLeft: 18,
        fontSize: 20,
        paddingBottom: 8,
        ...fullContainer
    },
    headerTextTwo: {
        position: 'relative',
        top: 12,
        left: 18,
        color: 'black',
        fontSize: 14,
        fontWeight: 600
    } as React.CSSProperties
};



// WEBPACK FOOTER //
// ./src/Pages/ProfileSettings/ProfileForm.tsx